import { useEffect, useMemo } from 'react';
import { Input, Select, Space } from 'antd';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useCountries } from '@core/hooks/useReference';
import { useStoreContext } from '@core/hooks/useStoreContext';

// eslint-disable-next-line react-refresh/only-export-components
export function validateMobileNumber(val, t) {
  if (!val?.number) {
    return Promise.reject(new Error(t('phoneRequired', 'Phone number is required')));
  }

  try {
    // calling_code is authoritative when present: a stored profile carries no
    // ISO country_code, and defaulting to IN would reject every non-Indian number.
    const callingDigits = String(val.calling_code || '').replace(/\D/g, '');
    const parsed = callingDigits
      ? parsePhoneNumberFromString(`+${callingDigits}${val.number}`)
      : parsePhoneNumberFromString(val.number, val.country_code || 'IN');
    if (!parsed || !parsed.isValid()) {
      return Promise.reject(
        new Error(t('invalidPhoneNumber', 'Please enter a valid phone number')),
      );
    }
  } catch {
    return Promise.reject(new Error(t('invalidPhoneNumber', 'Please enter a valid phone number')));
  }
  return Promise.resolve();
}

const DEFAULT_COUNTRY_CODE = 'IN';
const DEFAULT_COUNTRY_OPTION = {
  value: DEFAULT_COUNTRY_CODE,
  label: '🇮🇳 (+91)',
  name: 'India',
  callingCode: '+91',
  calling_code: '+91',
  country_code: 'IN',
  phoneLength: 15,
  code: 'IN',
};

function toFlagEmoji(code) {
  const str = String(code || '')
    .trim()
    .toUpperCase();
  if (!/^[A-Z]{2}$/.test(str)) return '🏳️';
  return String.fromCodePoint(...[...str].map((c) => 127397 + c.charCodeAt(0)));
}

// eslint-disable-next-line react-refresh/only-export-components
export function mapCountry(c) {
  const rawCode = c.cca2 || c.country_code || c.iso2 || c.iso_2 || c.code || c.value || '';
  const countryCode = String(rawCode)
    .trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .slice(0, 2);
  const dial = c.callingCode || c.calling_code || c.phone_code || c.dial_code || '';
  const callingCode = String(dial || '').replace(/^\+?/, '+');
  const countryName = c.name || c.country_name || c.common_name || countryCode;
  const flag = c.emoji || toFlagEmoji(countryCode);

  return {
    value: countryCode,
    label: `${flag} ${callingCode || ''}`.trim(),
    name: countryName,
    calling_code: callingCode,
    country_code: countryCode,
    phoneLength: c.phone_length || c.phoneLength || 15,
    code: countryCode,
    callingCode,
  };
}

export default function MobileNumberField({
  placeholder = 'Enter the Mobile Number',
  value = {},
  onChange,
}) {
  const countries = useCountries();
  // The store's own country (account preferences) is the default, not India.
  const { accountCountry } = useStoreContext();
  const defaultCountryCode = String(accountCountry || DEFAULT_COUNTRY_CODE)
    .trim()
    .toUpperCase();
  const countryOptions = useMemo(
    () => (Array.isArray(countries.data) ? countries.data.map(mapCountry) : []),
    [countries.data],
  );

  const selectedCountry = useMemo(
    () =>
      countryOptions.find((c) => c.value === value?.country) ||
      countryOptions.find((c) => c.country_code === value?.country_code) ||
      countryOptions.find((c) => c.calling_code === value?.calling_code) ||
      countryOptions.find((c) => c.value === defaultCountryCode) ||
      countryOptions.find((c) => c.value === DEFAULT_COUNTRY_CODE) ||
      countryOptions[0] ||
      DEFAULT_COUNTRY_OPTION,
    [countryOptions, defaultCountryCode, value?.calling_code, value?.country, value?.country_code],
  );

  // A value with no country yet is showing the default; write it back so the
  // form submits (and validates against) the calling code the user sees.
  const hasCountry = Boolean(value?.calling_code || value?.country_code || value?.country);
  useEffect(() => {
    if (hasCountry || !countryOptions.length || !selectedCountry?.calling_code) return;
    onChange?.({
      ...value,
      country: selectedCountry.value,
      country_name: selectedCountry.name,
      calling_code: selectedCountry.calling_code,
      country_code: selectedCountry.country_code,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCountry, countryOptions.length, selectedCountry]);

  const renderedCountryOptions = countryOptions.length ? countryOptions : [DEFAULT_COUNTRY_OPTION];
  const maxLength = selectedCountry?.phoneLength || 15;

  return (
    <Space.Compact style={{ width: '100%' }}>
      <Select
        showSearch
        value={selectedCountry?.value || DEFAULT_COUNTRY_CODE}
        optionLabelProp="label"
        onOpenChange={() => {
          // hook fetches eagerly via query; no-op to keep same UX contract
        }}
        onChange={(val) => {
          const selected =
            renderedCountryOptions.find((c) => c.value === val) || DEFAULT_COUNTRY_OPTION;
          onChange?.({
            ...value,
            country: selected.value,
            country_name: selected.name,
            calling_code: selected.calling_code,
            country_code: selected.country_code,
          });
        }}
        style={{ width: 140 }}
        optionFilterProp="label"
        filterOption={(input, option) => {
          const search = input.toLowerCase().replace('+', '');
          return (
            option?.label?.toLowerCase().includes(search) ||
            option?.name?.toLowerCase().includes(search) ||
            option?.code?.toLowerCase().includes(search) ||
            option?.callingCode?.replace('+', '').includes(search)
          );
        }}
        loading={countries.isLoading}
        options={renderedCountryOptions}
      />
      <Input
        value={value?.number || ''}
        onChange={(e) => {
          onChange?.({
            ...value,
            number: e.target.value,
          });
        }}
        onBlur={(e) => {
          const cleaned = e.target.value.replace(/\D/g, '');
          onChange?.({
            ...value,
            number: cleaned,
          });
        }}
        placeholder={placeholder}
        maxLength={maxLength}
        inputMode="tel"
      />
    </Space.Compact>
  );
}
